import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { LocationState } from 'contexts/LocationContext';
import { Battery, ElectricVehicle, Insulation, SolarPanel } from 'svg';
import { Location } from 'types';
import { HEATMAP_FILTER_VALUE, getLocationsHeatMapValue } from 'utils/map';
import { FunFacts } from '..';

const HEATMAP_OPTIONS = [
  {
    display: 'Installs for all products',
    value: HEATMAP_FILTER_VALUE.allProductInstalls
  },
  {
    display: 'Solar installs',
    value: HEATMAP_FILTER_VALUE.solarInstalls
  },
  {
    display: 'Battery installs',
    value: HEATMAP_FILTER_VALUE.batteryInstalls
  },
  {
    display: 'Solar & Battery installs',
    value: HEATMAP_FILTER_VALUE.solarBatteryInstalls
  },
  {
    display: 'Electric Vehicle Charger installs',
    value: HEATMAP_FILTER_VALUE.electricVehicleInstalls
  },
  {
    display: 'Electric Car (New)',
    value: HEATMAP_FILTER_VALUE.electricCarNewInstalls
  },
  {
    display: 'Electric Car (Used)',
    value: HEATMAP_FILTER_VALUE.electricCarUsedInstalls
  },
  {
    display: 'Electric Motorbike (New)',
    value: HEATMAP_FILTER_VALUE.electricMotorbikeNewInstalls
  },
  {
    display: 'Electric Motorbike (Used)',
    value: HEATMAP_FILTER_VALUE.electricMotorbikeUsedInstalls
  },
  {
    display: 'EV Charging Infrastructure installs',
    value: HEATMAP_FILTER_VALUE.evChargingInfrastructureInstalls
  },
  {
    display: 'Efficient Electric Stove Tops installs',
    value: HEATMAP_FILTER_VALUE.efficientElectricStoveTopsInstalls
  },
  {
    display: 'Electric Heating & Cooling Systems (Reverse Cycle) installs',
    value:
      HEATMAP_FILTER_VALUE.electricHeatingCoolingSystemsReverseCycleInstalls
  },
  {
    display: 'Hot Water Heat Pumps installs',
    value: HEATMAP_FILTER_VALUE.hotWaterHeatPumpInstalls
  },
  {
    display: 'Hydronic Heat Pumps installs',
    value: HEATMAP_FILTER_VALUE.hydronicHeatPumpInstalls
  },
  {
    display: 'Insulation installs',
    value: HEATMAP_FILTER_VALUE.insulationInstalls
  }
];

type PopularProductsPanelProps = {
  funFacts: FunFacts;
  setHeatMapFilter: (filter: string) => void;
  setLocation: Dispatch<SetStateAction<LocationState>>;
  onClose: () => void;
  locations: Location[];
};

export default function PopularProductsPanel({
  funFacts,
  setHeatMapFilter,
  setLocation,
  onClose,
  locations
}: PopularProductsPanelProps) {
  // set default option
  useEffect(() => {
    setHeatMapFilter(HEATMAP_OPTIONS[0].value);
  }, [setHeatMapFilter]);

  // hide options that don't have heat map values
  const heatMapOptions = useMemo(() => {
    return HEATMAP_OPTIONS.filter((option) => {
      const heatMapValues = getLocationsHeatMapValue(locations, option.value);
      const isHeatMapPresent = heatMapValues.some((value) => value !== null);

      return isHeatMapPresent;
    });
  }, [locations]);

  return (
    <div
      id="map-info-panel-products-panel"
      className="map-info-panel"
      role="dialog"
      aria-labelledby="map-info-panel-products-heading"
    >
      <div className="bg-primary p-4 text-white">
        <a
          href="javascript:void(0);"
          className="text-xs font-bold text-end underline underline-offset-2"
          onClick={onClose}
        >
          Close
        </a>
        <h3
          id="map-info-panel-products-heading"
          className="font-bold mt-4 text-white"
        >
          Popular products
        </h3>
      </div>
      <div className="map-info-panel-scroll">
        <div className="info-panel-child-sub-container">
          <p className="text-sm font-bold">Show heatmap for</p>
          <select
            name="popular-products-input-selector"
            id="popular-products-input-selector"
            className="info-panel-child-input-selector"
            onChange={(e) => setHeatMapFilter(e.target.value)}
            autoFocus
          >
            {heatMapOptions.map((option) => (
              <option key={option.display} value={option.value}>
                {option.display}
              </option>
            ))}
          </select>
          <div className="info-panel-child-gradient-progress-bar" />
          <div className="flexrow justify-between">
            <p className="text-xs uppercase">fewest</p>
            <p className="text-xs uppercase">most</p>
          </div>
        </div>
        <div className="space-y-4 p-4 pt-0">
          <div className="text-sm font-bold">Fun facts</div>

          {funFacts.mostSolarInstallationsLocation && (
            <div className="iconned-row flexrow">
              <SolarPanel />
              <div>
                <a
                  href="javascript:void(0);"
                  className="text-sm link-text-redirect"
                  onClick={() =>
                    setLocation(funFacts.mostSolarInstallationsLocation)
                  }
                >
                  {funFacts.mostSolarInstallationsLocation.location}
                </a>
                <span className="text-sm">
                  &nbsp;has the most solar installations (
                  {funFacts.mostSolarInstallationsValue}).
                </span>
              </div>
            </div>
          )}

          {funFacts.highestBatteryUptakeLocation && (
            <div className="iconned-row flexrow">
              <Battery />
              <div>
                <a
                  href="javascript:void(0);"
                  className="text-sm link-text-redirect"
                  onClick={() =>
                    setLocation(funFacts.highestBatteryUptakeLocation)
                  }
                >
                  {funFacts.highestBatteryUptakeLocation?.location}
                </a>
                <span className="text-sm">
                  &nbsp;has the highest battery uptake (
                  {funFacts.highestBatteryUptakeValue}%).
                </span>
              </div>
            </div>
          )}

          {funFacts.mostInsulationInstallationLocation && (
            <div className="iconned-row flexrow">
              <Insulation />
              <div>
                <a
                  href="javascript:void(0);"
                  className="text-sm link-text-redirect"
                  onClick={() =>
                    setLocation(funFacts.mostInsulationInstallationLocation)
                  }
                >
                  {funFacts.mostInsulationInstallationLocation.location}
                </a>
                <span className="text-sm">
                  &nbsp;has installed the most insulation (
                  {funFacts.mostInsulationInstallationValue}).
                </span>
              </div>
            </div>
          )}

          {funFacts.mostElectricVehiclePurchaseLocation && (
            <div className="iconned-row flexrow">
              <ElectricVehicle />
              <div>
                <a
                  href="javascript:void(0);"
                  className="text-sm link-text-redirect"
                  onClick={() =>
                    setLocation(funFacts.mostElectricVehiclePurchaseLocation)
                  }
                >
                  {funFacts.mostElectricVehiclePurchaseLocation.location}
                </a>
                <span className="text-sm">
                  &nbsp;has purchased the most electric vehicles (
                  {funFacts.mostElectricVehiclePurchaseValue}).
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
